import { useState } from "react";
import { Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { buscarHtmlEnviado, useLeadEmailsEnviados, type EmailEnviadoLead } from "@/hooks/useLeadEmailsEnviados";

const dataHora = (s: string | null) => (s ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

const rotuloSerie = (e: EmailEnviadoLead) => {
  const m = String(e.modelo || e.campanha || "");
  return m.replace(/^Port CNPJ · /i, "PORT CNPJ · ").replace(/^Fixa Avançada · /i, "FIXA AVANÇADA · ");
};

function Situacao({ e }: { e: EmailEnviadoLead }) {
  return (
    <span className="text-[11px] text-muted-foreground">
      {dataHora(e.enviado_em)} · {e.abriu ? <b className="font-semibold text-foreground">abriu</b> : "não abriu"}
      {e.clicou ? <> · <b className="font-semibold text-primary">clicou {dataHora(e.clicou_em)}</b></> : null}
      {e.assinado ? <> · assinado por {e.assinado}</> : null}
    </span>
  );
}

export function LeadPropostaEnviadaEmail({ leadId }: { leadId: string }) {
  const { data } = useLeadEmailsEnviados(leadId);
  const [html, setHtml] = useState<string | null>(null);
  const [abrindo, setAbrindo] = useState(false);
  if (!data?.length) return null;
  const [atual, ...outros] = data;

  const abrir = async (id: string) => {
    setAbrindo(true);
    try {
      setHtml((await buscarHtmlEnviado(id)) || "<p style='font-family:Arial;padding:16px'>Este e-mail saiu antes de o CRM guardar a cópia.</p>");
    } finally {
      setAbrindo(false);
    }
  };

  return (
    <section className="overflow-hidden rounded-lg border border-primary/60 bg-card">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-primary">Proposta enviada por e-mail</span>
        <span className="rounded-md bg-primary px-2 py-0.5 text-[10.5px] font-semibold text-primary-foreground">{rotuloSerie(atual)}</span>
      </header>
      {atual.caixa_enviada ? (
        <div className="px-3 py-2 [&_table]:w-full" dangerouslySetInnerHTML={{ __html: atual.caixa_enviada }} />
      ) : (
        <p className="px-3 py-2 text-xs text-muted-foreground">Este e-mail não levou tabela de proposta. Abra o e-mail para ver o texto.</p>
      )}
      <footer className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2">
        <Situacao e={atual} />
        <button type="button" onClick={() => abrir(atual.id)} disabled={abrindo}
          className="inline-flex h-7 items-center gap-1 rounded-md border border-primary px-2.5 text-xs font-semibold text-primary hover:bg-primary/5">
          <Mail className="h-3.5 w-3.5" /> Ver o e-mail enviado
        </button>
      </footer>
      {outros.length ? (
        <div className="border-t px-3 py-2">
          <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">Outros e-mails</div>
          {outros.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 py-1 text-xs">
              <span className="font-medium text-foreground">{rotuloSerie(e)}</span>
              <span className="flex items-center gap-2"><Situacao e={e} />
                <button type="button" onClick={() => abrir(e.id)} className="text-[11px] font-semibold text-primary hover:underline">ver</button></span>
            </div>
          ))}
        </div>
      ) : null}
      <Dialog open={html !== null} onOpenChange={(o) => { if (!o) setHtml(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>E-mail que o cliente recebeu</DialogTitle></DialogHeader>
          <iframe title="E-mail enviado" sandbox="" srcDoc={html || ""} className="h-[70vh] w-full rounded-md border bg-white" />
        </DialogContent>
      </Dialog>
    </section>
  );
}
